/**
 * Admin Commercial Dashboard.
 * Displays the three-channel revenue snapshot for the Commercial Launch
 * Control Board (CLCB). Reads /api/commercial/dashboard.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function CommercialDashboard() {
  const [data, setData] = useState(null);
  const [gates, setGates] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get(`/commercial/dashboard`),
      api.get(`/commercial/clcb/gates`),
      api.get(`/commercial/clcb/launch-readiness`),
    ]).then(([d, g, r]) => {
      if (d.status === "fulfilled") setData(d.value.data);
      else setErr(d.reason?.message || "dashboard load failed");
      if (g.status === "fulfilled") setGates(g.value.data.gates || []);
      if (r.status === "fulfilled") setReadiness(r.value.data);
    });
  }, []);

  const flipGate = async (gate_id, status, extra = {}) => {
    try {
      await api.post(`/commercial/clcb/gates/${gate_id}/set`, { status, evidence: "", ...extra });
      const [gs, rd] = await Promise.all([
        api.get(`/commercial/clcb/gates`),
        api.get(`/commercial/clcb/launch-readiness`),
      ]);
      setGates(gs.data.gates || []);
      setReadiness(rd.data);
    } catch (e) {
      setErr(e?.response?.data?.detail?.code || e.message);
    }
  };

  if (err) return <div className="text-rose-300" data-testid="commercial-dashboard-error">{String(err)}</div>;
  if (!data) return <div className="text-white/60" data-testid="commercial-dashboard-loading">Loading…</div>;

  const { channels, risk, as_of } = data;
  const totalArr = (channels.citizen.arr_usd || 0) + (channels.institutional.arr_usd || 0);

  return (
    <div className="space-y-8" data-testid="commercial-dashboard">
      <header>
        <h1 className="text-2xl font-semibold text-white" data-testid="commercial-heading">Commercial Dashboard</h1>
        <p className="text-white/50 text-xs mt-1">
          Approved Version 1.0 commercial model · {new Date(as_of).toLocaleString()}
        </p>
      </header>

      {/* Top-line totals */}
      <section className="grid gap-4 md:grid-cols-3">
        <Card label="Total ARR" value={fmtUsd(totalArr)} testid="commercial-total-arr" />
        <Card label="Citizen ARR" value={fmtUsd(channels.citizen.arr_usd)} sub={`${channels.citizen.active_subscribers} active subs`} testid="commercial-citizen-arr" />
        <Card label="Institutional ARR" value={fmtUsd(channels.institutional.arr_usd)} testid="commercial-inst-arr" />
      </section>

      {/* Institutional breakdown */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="commercial-inst-breakdown">
        <h2 className="text-white/80 text-sm uppercase tracking-widest mb-4">Institutional plans</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {["starter", "growth", "enterprise"].map((plan) => (
            <div key={plan} className="rounded-xl border border-white/10 p-4" data-testid={`commercial-plan-${plan}`}>
              <div className="text-white capitalize text-lg font-semibold">{plan}</div>
              <div className="mt-3 text-white/70 text-xs space-y-1">
                {["active", "past_due", "canceled", "inactive"].map((k) => (
                  <div key={k} className="flex justify-between">
                    <span>{k}</span>
                    <span data-testid={`commercial-plan-${plan}-${k}`}>{channels.institutional.by_plan[plan]?.[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Platform + risk */}
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="commercial-platform">
          <h2 className="text-white/80 text-sm uppercase tracking-widest mb-3">Platform overage (this period)</h2>
          <div className="flex items-baseline gap-3">
            <div className="text-2xl text-white font-semibold" data-testid="commercial-overage-calls">
              {channels.platform.overage_calls_this_period.toLocaleString()}
            </div>
            <div className="text-white/50 text-sm">calls</div>
          </div>
          <div className="mt-2 text-white/70 text-sm" data-testid="commercial-overage-revenue">
            {fmtUsd(channels.platform.overage_revenue_usd_this_period)} projected
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="commercial-risk">
          <h2 className="text-white/80 text-sm uppercase tracking-widest mb-3">Risk</h2>
          <div className="text-white/70 text-sm space-y-1">
            <div className="flex justify-between">
              <span>Disputes (all time)</span>
              <span data-testid="commercial-disputes">{risk.disputes_all_time}</span>
            </div>
            <div className="flex justify-between">
              <span>Refunds (all time)</span>
              <span data-testid="commercial-refunds">{risk.refunds_all_time}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CLCB gates */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="commercial-clcb">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white/80 text-sm uppercase tracking-widest">Launch Control Board — Go/No-Go gates</h2>
          {readiness && (
            <div
              className={`px-3 py-1 rounded-full text-xs uppercase tracking-widest font-semibold border ${
                readiness.readiness === "GO"
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                  : "bg-amber-500/20 text-amber-300 border-amber-500/40"
              }`}
              data-testid="clcb-readiness-status"
            >
              {readiness.readiness} · {readiness.gates_approved}/{readiness.gates_total} gates
            </div>
          )}
        </div>
        <div className="space-y-3">
          {gates.map((g) => (
            <GateRow key={g.gate_id} gate={g} onFlip={flipGate} />
          ))}
        </div>
        <div className="mt-4 text-white/40 text-xs">
          <a
            className="underline hover:text-white/70"
            href="/docs/release/clcb-gates-runbook.md"
            target="_blank"
            rel="noopener"
            data-testid="clcb-runbook-link"
          >
            Open gate runbook
          </a>
          {" · "}
          <a
            className="underline hover:text-white/70"
            href="/docs/release/launch-execution-runbook-v1.md"
            target="_blank"
            rel="noopener"
            data-testid="launch-runbook-link"
          >
            Open launch execution runbook
          </a>
        </div>
      </section>
    </div>
  );
}

function GateRow({ gate, onFlip }) {
  const [evidenceUrl, setEvidenceUrl] = React.useState(gate.evidence_url || "");
  const [notes, setNotes] = React.useState(gate.notes || "");
  const [status, setStatus] = React.useState(gate.status);
  const [saving, setSaving] = React.useState(false);
  const [history, setHistory] = React.useState(null);

  React.useEffect(() => { setStatus(gate.status); }, [gate.status]);

  const save = async () => {
    setSaving(true);
    await onFlip(gate.gate_id, status, { evidence_url: evidenceUrl, notes });
    setSaving(false);
  };

  const showHistory = async () => {
    if (history) { setHistory(null); return; }
    const { data } = await api.get(`/commercial/clcb/gates/${gate.gate_id}/history`);
    setHistory(data.history || []);
  };

  const statusColor = {
    approved: "text-emerald-300",
    pending: "text-amber-300",
    blocked: "text-rose-300",
  }[status] || "text-white/70";

  return (
    <div className="border-b border-white/5 pb-3" data-testid={`clcb-gate-${gate.gate_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm">
            <span className="text-white/40 mr-2">#{gate.gate_id}</span>
            {gate.name}
          </div>
          <div className={`text-xs mt-1 ${statusColor}`}>
            Owner: {gate.owner || "—"} · Last updated: {gate.updated_at ? new Date(gate.updated_at).toLocaleString() : "—"} {gate.updated_by ? `· by ${gate.updated_by}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="bg-black/40 border border-white/20 rounded px-2 py-1 text-xs text-white"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`clcb-gate-select-${gate.gate_id}`}
          >
            <option value="pending">pending</option>
            <option value="approved">approved</option>
            <option value="blocked">blocked</option>
          </select>
          <button
            className="text-xs bg-white/10 hover:bg-white/20 rounded px-2 py-1 text-white"
            onClick={save}
            disabled={saving}
            data-testid={`clcb-gate-save-${gate.gate_id}`}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            className="text-xs text-white/50 hover:text-white/80"
            onClick={showHistory}
            data-testid={`clcb-gate-history-${gate.gate_id}`}
          >
            {history ? "Hide" : "History"}
          </button>
        </div>
      </div>
      <div className="mt-2 flex gap-2 flex-wrap">
        <input
          type="url"
          placeholder="Evidence URL"
          value={evidenceUrl}
          onChange={(e) => setEvidenceUrl(e.target.value)}
          className="flex-1 min-w-[240px] bg-black/40 border border-white/20 rounded px-2 py-1 text-xs text-white placeholder-white/30"
          data-testid={`clcb-gate-evidence-${gate.gate_id}`}
        />
        <input
          type="text"
          placeholder="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="flex-1 min-w-[200px] bg-black/40 border border-white/20 rounded px-2 py-1 text-xs text-white placeholder-white/30"
          data-testid={`clcb-gate-notes-${gate.gate_id}`}
        />
      </div>
      {history && (
        <div className="mt-2 rounded border border-white/10 bg-black/40 p-2 text-xs" data-testid={`clcb-gate-history-list-${gate.gate_id}`}>
          {history.length === 0 && <div className="text-white/40">No history yet.</div>}
          {history.map((h, i) => (
            <div key={i} className="text-white/60 flex justify-between gap-4 py-1 border-b border-white/5 last:border-0">
              <span className="w-40 shrink-0">{new Date(h.recorded_at).toLocaleString()}</span>
              <span className={`${{
                approved: "text-emerald-300",
                pending: "text-amber-300",
                blocked: "text-rose-300",
              }[h.status] || "text-white/70"} w-20 shrink-0`}>{h.status}</span>
              <span className="flex-1 truncate">
                {h.approver || "—"} · {h.evidence_url || h.evidence || "—"} {h.notes ? `— ${h.notes}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtUsd(v) {
  return v?.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }) || "$0";
}

function Card({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-2 text-3xl font-semibold text-white">{value}</div>
      {sub && <div className="text-white/50 text-xs mt-1">{sub}</div>}
    </div>
  );
}
